import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button } from "@mui/material";
import { useSnackbar } from "notistack";
import {
  listFiles,
  uploadFile,
  createFolder,
  presignFile,
  type Project,
  type StoreObject,
  type StoreTags,
} from "../../lib/brain-api";
import { brainTokens } from "../../design/brainTheme";
import { SectionCard, Icon } from "../../mui";
import { fmtBytes } from "../../mui/format";

const c = brainTokens.color;

interface Entry {
  kind: "folder" | "file";
  name: string;
  key: string;
  size?: number;
}

export function CustomerFilesTabMui({ customerId, projects: _projects }: { customerId: string; projects: Project[] }) {
  const { enqueueSnackbar } = useSnackbar();
  const base = `customers/${customerId}/`;
  const [path, setPath] = useState("");
  const [objects, setObjects] = useState<StoreObject[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const prefix = base + path;
  const tags: StoreTags = useMemo(() => ({ customer: customerId, source: "upload", tier: "company" }), [customerId]);

  const load = useCallback(async () => {
    try {
      setObjects(await listFiles(base));
    } catch {
      enqueueSnackbar("Failed to load files.", { variant: "error" });
    }
  }, [base, enqueueSnackbar]);
  useEffect(() => {
    void load();
  }, [load]);

  // Derive immediate children of the current path from the flat object list.
  const entries = useMemo(() => {
    const folders = new Map<string, Entry>();
    const files: Entry[] = [];
    for (const o of objects) {
      if (!o.key.startsWith(prefix)) continue;
      const rest = o.key.slice(prefix.length);
      if (!rest || rest === ".keep") continue;
      const slash = rest.indexOf("/");
      if (slash === -1) {
        files.push({ kind: "file", name: rest, key: o.key, size: o.size });
      } else {
        const name = rest.slice(0, slash);
        if (!folders.has(name)) folders.set(name, { kind: "folder", name, key: `${prefix}${name}/` });
      }
    }
    return [...[...folders.values()].sort((a, b) => a.name.localeCompare(b.name)), ...files.sort((a, b) => a.name.localeCompare(b.name))];
  }, [objects, prefix]);

  const upload = async (files: FileList | File[]) => {
    setBusy(true);
    try {
      for (const f of Array.from(files)) {
        await uploadFile(`${prefix}${f.name}`, f, tags);
      }
      enqueueSnackbar(`Uploaded ${Array.from(files).length} file(s).`, { variant: "success" });
      await load();
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : "Upload failed.", { variant: "error" });
    } finally {
      setBusy(false);
    }
  };

  const newFolder = async () => {
    const name = window.prompt("New folder name")?.trim();
    if (!name) return;
    try {
      await createFolder(`${prefix}${name}/`, tags);
      await load();
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : "Create folder failed.", { variant: "error" });
    }
  };

  const openFile = async (key: string) => {
    try {
      window.open(await presignFile(key), "_blank", "noopener");
    } catch {
      enqueueSnackbar("Could not open file.", { variant: "error" });
    }
  };

  const crumbs = ["root", ...path.split("/").filter(Boolean)];

  return (
    <SectionCard
      title="Files"
      actions={
        <>
          <Button variant="outlined" onClick={() => void newFolder()} startIcon={<Icon name="folder-plus" size={15} stroke={1.8} />}>
            New folder
          </Button>
          <Button variant="contained" disabled={busy} onClick={() => fileInput.current?.click()} startIcon={<Icon name="upload" size={15} stroke={1.8} />}>
            Upload
          </Button>
          <input ref={fileInput} type="file" multiple hidden onChange={(e) => e.target.files && void upload(e.target.files)} />
        </>
      }
      headerMb={20}
    >
      {/* breadcrumb (only when navigated into a subfolder) */}
      {path && (
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px", mb: "12px", fontSize: 12.5, color: c.muted2, flexWrap: "wrap" }}>
          {crumbs.map((seg, i) => (
            <Box component="span" key={i} sx={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
              {i > 0 && <Box component="span" sx={{ color: c.placeholder }}>›</Box>}
              <Box
                component="span"
                onClick={() => setPath(crumbs.slice(1, i + 1).join("/") + (i > 0 ? "/" : ""))}
                sx={{ cursor: "pointer", fontFamily: brainTokens.font.mono, "&:hover": { color: c.tealInk } }}
              >
                {seg}
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {entries.length === 0 && <Box sx={{ fontSize: 13, color: c.muted2, mb: "6px" }}>Empty folder.</Box>}
      <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        {entries.map((e) => (
          <Box
            key={e.key}
            onClick={() => (e.kind === "folder" ? setPath(e.key.slice(base.length)) : void openFile(e.key))}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "11px",
              padding: "14px 16px",
              border: `1px solid ${c.borderLight}`,
              borderRadius: "8px",
              fontSize: 14,
              fontWeight: 500,
              cursor: "pointer",
              "&:hover": { background: c.panelBg3 },
            }}
          >
            <Box sx={{ color: e.kind === "folder" ? c.tealInk2 : c.muted2, display: "flex", flex: "none" }}>
              <Icon name={e.kind === "folder" ? "folder" : "book"} size={18} stroke={1.7} />
            </Box>
            <Box sx={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.name}</Box>
            {e.kind === "file" && e.size != null && (
              <Box component="span" sx={{ fontFamily: brainTokens.font.mono, fontSize: 12, color: c.faint }}>
                {fmtBytes(e.size)}
              </Box>
            )}
          </Box>
        ))}
      </Box>

      {/* drop zone */}
      <Box
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          if (e.dataTransfer.files.length) void upload(e.dataTransfer.files);
        }}
        sx={{
          mt: "14px",
          border: `1.5px dashed ${dragOver ? c.teal : "#d7dce0"}`,
          borderRadius: "10px",
          padding: "34px",
          textAlign: "center",
          fontSize: 13.5,
          color: dragOver ? c.tealInk : c.faint,
          background: dragOver ? c.tealTint2 : "transparent",
        }}
      >
        Drop files to upload into <Box component="span" sx={{ fontFamily: brainTokens.font.mono, fontSize: 12 }}>{path || "root"}</Box> — auto-tagged.
      </Box>
    </SectionCard>
  );
}
